import React, { useState } from 'react';
import { enterprisePlan } from '../../../../../services/authService';
import { toast } from "react-toastify";
import { emailRegex, phoneRegex } from '../../../../../hooks/useFormValidation';

const EnterprisePlanModal = ({ plan, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    adsAccounts: '',
    sellerAccounts: '',
    supportRequirement: '',
    orgSize: '',
    orgName: '',
    orgAddress: '',
    orgEmail: '',
    orgPhone: '',
    message: '',
    contactName: '',
    contactPhone: '',
    contactEmail: ''
  });

  const [errors, setErrors] = useState({});

  const totalSteps = 4;

  // Generate dropdown options
  const generateNumberOptions = (max) => {
    return Array.from({ length: max }, (_, i) => i + 1);
  };

  const orgSizeRanges = [
    '1-10',
    '11-25',
    '26-50',
    '51-100',
    '101-250',
    '251-500',
    '500+'
  ];

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.adsAccounts) newErrors.adsAccounts = 'Please select number of accounts';
      if (!formData.supportRequirement) newErrors.supportRequirement = 'Please select a support requirement';
    }

    if (step === 2) {
      if (!formData.orgSize) newErrors.orgSize = 'Please select organization size';

      if (!formData.orgName.trim()) {
        newErrors.orgName = "Organization name is required";
      }
      else if (!/^(?!\d+$)[A-Za-z0-9\s&.,-]{2,50}$/.test(formData.orgName.trim())) {
        newErrors.orgName = "If you use numbers, at least one letter is required. Only letters, numbers, . , - & allowed.";
      }
      else if (formData.orgName.trim().length < 3 || formData.orgName.trim().length > 50) {
        newErrors.orgName = "Organization name must be 3–50 characters";
      }

      if (!formData.orgAddress) {
        newErrors.orgAddress = "Organization address is required";
      }
      else if (!/^[A-Za-z\s]+$/.test(formData.orgAddress)) {
        newErrors.orgAddress = "Only letters and spaces allowed";
      }
      else if (formData.orgAddress.length > 100) {
        newErrors.orgAddress = "Organization address must be less than 100 characters";
      }

      if (!formData.orgEmail)
        newErrors.orgEmail = "Organization email is required";
      else if (!emailRegex.test(formData.orgEmail))
        newErrors.orgEmail = "Please enter a valid email";

      // OPTIONAL ---- orgPhone
      if (formData.orgPhone && !/^[0-9]{10}$/.test(formData.orgPhone)) {
        newErrors.orgPhone = "Enter a valid phone number (10 digits only)";
      }

    }


    if (step === 3) {
      if (!formData.contactName) newErrors.contactName = 'Contact name is required';
      if (!formData.contactName)
        newErrors.contactName = "Contact Person name is required";
      else if (!/^[A-Za-z\s]+$/.test(formData.contactName))
        newErrors.contactName = "Only letters and spaces allowed";
      else if (formData.contactName.length <= 2 || formData.contactName.length > 20)
        newErrors.contactName = "Name must be 3-20 characters";

      // Required ---- contactPhone
      if (!formData.contactPhone) {
        newErrors.contactPhone = "Contact number is required";
      } else if (!/^[0-9]{10,12}$/.test(formData.contactPhone)) {
        newErrors.contactPhone = "Enter a valid phone number (10–12 digits only)";
      }

      // OPTIONAL ---- contactEmail
      if (formData.contactEmail && !emailRegex.test(formData.contactEmail)) {
        newErrors.contactEmail = "Invalid email format";
      }

      // if (!formData.phone)
      //   newErrors.phone = "Phone number is required";
      // else if (!phoneRegex.test(formData.phone))
      //   newErrors.phone = "Phone must be exactly 10 digits";

      // if (!formData.email)
      //   newErrors.email = "Email is required";
      // else if (!emailRegex.test(formData.email))
      //   newErrors.email = "Invalid email format";
      // 
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateField = (field, value) => {

    let error = "";

    switch (field) {
      case "adsAccounts":
        if (!value) error = "Please select number of accounts";
        break;

      case "supportRequirement":
        if (!value) error = "Please select a support requirement";
        break;

      case "orgSize":
        if (!value) error = "Please select organization size";
        break;

      case "orgName":
        if (!value.trim()) {
          error = "Organization name is required";
        } else if (!/^(?!\d+$)[A-Za-z0-9\s&.,-]{2,50}$/.test(value.trim())) {
          error = "If you use numbers, at least one letter is required. Only letters, numbers, . , - & allowed.";
        }
        break;

      case "orgAddress":
        if (!value) error = "Organization address is required";
        else if (!/^[A-Za-z\s]+$/.test(value))
          error = "Only letters and spaces allowed";
        else if (value.length > 100)
          error = "Max 100 characters";
        break;

      case "orgEmail":
        if (!value) error = "Email is required";
        else if (!emailRegex.test(value)) error = "Invalid email format";
        break;
      
      case "contactPhone":
      case "orgPhone":
        if (value.trim() !== "" && !/^\d{10}$/.test(value)) {
          error = "Enter a valid phone number (10 digits only)";
        }
        break;

      case "contactName":
        if (!value) error = "Contact person name is required";
        else if (!/^[A-Za-z\s]+$/.test(value))
          error = "Only letters and spaces allowed";
        else if (value.length <= 2 || value.length > 20)
          error = "Name must be 3–20 characters";
        break;

      case "contactEmail":
        if (value && !emailRegex.test(value))
          error = "Invalid email format";
        break;

      default:
        break;
    }

    return error;
  };


  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
    }
  };


  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    try {
      setCurrentStep(4);

      const finalPayload = {
        step1: {
          amazon_ads_accounts_managed: Number(formData.adsAccounts),
          preferred_support:
            formData.supportRequirement === "premium"
              ? "Dedicated Account Manager & 24/7 Priority Support"
              : "Standard business hours support"
        },
        step2: {
          organization_size: `${formData.orgSize} Employees`,
          organization_details: {
            organization_name: formData.orgName,
            address_city: formData.orgAddress,
            organization_email: formData.orgEmail,
            organization_phone: formData.orgPhone,
            requirements_message: formData.message
          }
        },
        step3: {
          contact_person: {
            name: formData.contactName,
            phone: formData.contactPhone,
            email: formData.contactEmail
          }
        }
      };


      // ---- API CALL ----
      const response = await enterprisePlan(finalPayload);

      // ---- SUCCESS TOAST ----
      toast.success(response?.message || "Saved successfully!", {
        position: "bottom-right",
      })
    } catch (error) {
      console.error("Enterprise Plan Submit Error: ", error);

      // ---- ERROR TOAST ----
      toast.error(
        error?.response?.message ||
        "Something went wrong, please try again."
      );
    }
  };

  const handleInputChange = (field, value) => {
    let newValue = value;
   
    setFormData(prev => ({ ...prev, [field]: value }));
    // validate this field
    const errorMsg = validateField(field, newValue);

    setErrors(prev => ({
      ...prev,
      [field]: errorMsg
    }));
  };

  const renderStep1 = () => (
    <div>
      <div className="mb-4">
        <label className="enterprise-label">
          How many Amazon accounts do you have? <span className="text-danger">*</span>
        </label>
        <select
          value={formData.adsAccounts}
          onChange={(e) => handleInputChange('adsAccounts', e.target.value)}
          className={`enterprise-select ${errors.adsAccounts ? 'is-invalid' : ''}`}
        >
          <option value="">Select number of accounts</option>
          {generateNumberOptions(50).map(num => (
            <option key={num} value={num}>{num}</option>
          ))}
        </select>
        {errors.adsAccounts && <div className="enterprise-error">{errors.adsAccounts}</div>}
      </div>


      <div className="mb-3">
        <label className="enterprise-label">
          What is your preferred support requirement? <span className="text-danger">*</span>
        </label>
        <div className="d-flex flex-column gap-3">
          <label className={`enterprise-radio-card ${formData.supportRequirement === 'standard' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="support"
              value="standard"
              checked={formData.supportRequirement === 'standard'}
              onChange={(e) => handleInputChange('supportRequirement', e.target.value)}
              className="enterprise-radio-input"
            />
            <span className="enterprise-radio-text">Standard business hours support</span>
          </label>
          <label className={`enterprise-radio-card ${formData.supportRequirement === 'premium' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="support"
              value="premium"
              checked={formData.supportRequirement === 'premium'}
              onChange={(e) => handleInputChange('supportRequirement', e.target.value)}
              className="enterprise-radio-input"
            />
            <span className="enterprise-radio-text">Dedicated Account Manager & 24/7 Priority Support</span>
          </label>
        </div>
        {errors.supportRequirement && <div className="enterprise-error mt-2">{errors.supportRequirement}</div>}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <div className="mb-4">
        <label className="enterprise-label">
          What is your organization's estimated size? <span className="text-danger">*</span>
        </label>
        <select
          value={formData.orgSize}
          onChange={(e) => handleInputChange('orgSize', e.target.value)}
          className={`enterprise-select ${errors.orgSize ? 'is-invalid' : ''}`}
        >
          <option value="">Select organization size</option>
          {orgSizeRanges.map(range => (
            <option key={range} value={range}>{range} Employees</option>
          ))}
        </select>
        {errors.orgSize && <div className="enterprise-error">{errors.orgSize}</div>}
      </div>

      <div className="enterprise-section">
        <h5 className="enterprise-section-title">Organization Details</h5>

        <div className="mb-3">
          <label className="enterprise-label">
            Organization Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={formData.orgName}
            onChange={(e) => handleInputChange('orgName', e.target.value)}
            className={`enterprise-input ${errors.orgName ? 'is-invalid' : ''}`}
            placeholder="Enter organization name"
          />
          {errors.orgName && <div className="enterprise-error">{errors.orgName}</div>}
        </div>

        <div className="mb-3">
          <label className="enterprise-label">
            Organization Address (City/Country) <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            value={formData.orgAddress}
            onChange={(e) => handleInputChange('orgAddress', e.target.value)}
            className={`enterprise-input ${errors.orgAddress ? 'is-invalid' : ''}`}
            placeholder="City, Country"
          />
          {errors.orgAddress && <div className="enterprise-error">{errors.orgAddress}</div>}
        </div>

        <div className="mb-3">
          <label className="enterprise-label">
            Organization Email Address <span className="text-danger">*</span>
          </label>
          <input
            type="email"
            value={formData.orgEmail}
            onChange={(e) => handleInputChange('orgEmail', e.target.value)}
            className={`enterprise-input ${errors.orgEmail ? 'is-invalid' : ''}`}
            placeholder="contact@organization.com"
          />
          {errors.orgEmail && <div className="enterprise-error">{errors.orgEmail}</div>}
        </div>

        <div className="mb-3">
          <label className="enterprise-label">
            Organization Phone <span className="">(Optional)</span>
          </label>
          <input
            type="text"
            value={formData.orgPhone}
            onChange={(e) => handleInputChange('orgPhone', e.target.value)}
            className="enterprise-input"
            placeholder="9876543210" />
          {errors.orgPhone && <div className="enterprise-error">{errors.orgPhone}</div>}
        </div>

        <div className="mb-3">
          <label className="enterprise-label">
            Your Message / Needs
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            rows={4}
            className="enterprise-textarea"
            placeholder="Tell us about your analytics needs..."
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h5 className="enterprise-section-title mb-4">Contact Person Details</h5>

      <div className="mb-3">
        <label className="enterprise-label">
          Contact Person Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          value={formData.contactName}
          onChange={(e) => handleInputChange('contactName', e.target.value)}
          className={`enterprise-input ${errors.contactName ? 'is-invalid' : ''}`}
          placeholder="John Doe"
        />
        {errors.contactName && <div className="enterprise-error">{errors.contactName}</div>}
      </div>

      <div className="mb-3">
        <label className="enterprise-label">
          Contact Person Phone <span className="text-danger">*</span>
        </label>
        <input
          type="tel"
          value={formData.contactPhone}
          onChange={(e) => handleInputChange('contactPhone', e.target.value)}
          className="enterprise-input"
          placeholder="9876543210"
        />
        {errors.contactPhone && <div className="enterprise-error">{errors.contactPhone}</div>}
      </div>

      <div className="mb-3">
        <label className="enterprise-label">
          Contact Email <span className="">(Optional)</span>
        </label>
        <input
          type="email"
          value={formData.contactEmail}
          onChange={(e) => handleInputChange('contactEmail', e.target.value)}
          className="enterprise-input"
          placeholder="john@example.com"
        />
        {errors.contactEmail && <div className="enterprise-error">{errors.contactEmail}</div>}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="text-center py-4">
      <div className="enterprise-success-icon">
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
      </div>

      <h2 className="enterprise-success-title mb-4">
        Demo Requested, {formData.contactName}!
      </h2>

      <div className="enterprise-success-card">
        <p className="enterprise-success-text mb-3">
          We have successfully recorded your details and will contact you at{' '}
          <span className="enterprise-highlight">{formData.orgEmail}</span>{' '}
          within 1 business hour.
        </p>

        <p className="enterprise-success-text mb-0">
          We look forward to discussing your analytics needs!
        </p>
      </div>

      <div className="mt-4">
        <button onClick={onClose} className="enterprise-btn-close">
          Close
        </button>
      </div>
    </div>
  );

  return (
    <>


      <div className="enterprise-modal-overlay">
        <div className="enterprise-modal-container">
          {/* Header */}
          <div className="enterprise-modal-header">
            <div>
              <h2 className="enterprise-modal-title">Enterprise Plan</h2>
              {currentStep < 4 && (

                <p className="enterprise-modal-subtitle mb-0">
                  Step {currentStep} of {totalSteps - 1}
                </p>
              )}
            </div>
            <button onClick={onClose} className="enterprise-close-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          {currentStep < 4 && (
            <div className="enterprise-progress-container">
              <div className="enterprise-progress-bar">
                <div
                  className="enterprise-progress-fill"
                  style={{ width: `${(currentStep / (totalSteps - 1)) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Body */}
          <div className="enterprise-modal-body">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </div>
          <div className='enterprise-modal-success-body'>
            {currentStep === 4 && renderStep4()}
          </div>
          {/* Footer */}
          {currentStep < 4 && (
            <div className="enterprise-modal-footer">
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className="enterprise-btn enterprise-btn-back"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
                Back
              </button>

              <button
                onClick={currentStep === 3 ? handleSubmit : handleNext}
                className="enterprise-btn enterprise-btn-next"
              >
                {currentStep === 3 ? 'Submit' : 'Next'}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EnterprisePlanModal;